import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Slide,
  Paper
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { Close as CloseIcon, SendOutlined, ReportProblemOutlined, MessageOutlined, CheckOutlined } from '@mui/icons-material';
import { Message } from '../types';

interface ReportModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (message: string) => Promise<void>;
  messages: Message[];
  restaurantName: string;
  tableNumber: string;
  serverName: string;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ReportModal: React.FC<ReportModalProps> = ({
  open,
  onClose,
  onSubmit,
  restaurantName,
  tableNumber,
  serverName
}) => {
  const [reportMessage, setReportMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const maxChars = 500;
  const remainingChars = maxChars - reportMessage.length;

  // Reset all states when modal opens
  useEffect(() => {
    if (open) {
      setReportMessage('');
      setIsSubmitting(false);
      setError(null);
      setShowSuccess(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!reportMessage.trim()) {
      setError('Please share your feedback');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(reportMessage.trim());
      
      // Reset submitting state and show success feedback
      setIsSubmitting(false);
      setShowSuccess(true);
      
      // Close modal after showing success for 2 seconds
      setTimeout(() => {
        setReportMessage('');
        setShowSuccess(false);
        onClose();
      }, 2000);
      
    } catch (err) {
      setError('Unable to submit report. Please notify staff in person.');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting && !showSuccess) {
      setReportMessage('');
      setError(null);
      setShowSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          margin: 2,
          maxHeight: 'calc(100vh - 32px)'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Paper elevation={0} sx={{ borderRadius: 3 }}>
          {/* Header */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                color: 'white',
                p: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <MessageOutlined sx={{ fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Message Management
                </Typography>
              </Box>
            <IconButton
              onClick={handleClose}
              disabled={isSubmitting}
              sx={{ 
                color: 'white',
                '&:hover': { 
                  backgroundColor: 'rgba(255,255,255,0.1)' 
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3 }}>

            {/* Message Input */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Share your feedback
              </Typography>
              <TextField
                multiline
                rows={6}
                fullWidth
                value={reportMessage}
                onChange={(e) => {
                  if (e.target.value.length <= maxChars) {
                    setReportMessage(e.target.value);
                    setError(null);
                  }
                }}
                placeholder="Share your experience with Management - whether it's a compliment for great service or an issue that needs attention..."
                variant="outlined"
                disabled={isSubmitting}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: (theme) => 
                      theme.palette.mode === 'light' ? '#fafafa' : '#2a2a2a',
                    '&:hover': {
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light' ? '#f5f5f5' : '#333333',
                    },
                    '&.Mui-focused': {
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light' ? 'white' : '#1a1a1a',
                    }
                  },
                  '& .MuiInputBase-input': {
                    color: (theme) => theme.palette.text.primary
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: (theme) => theme.palette.text.secondary,
                    opacity: 0.7
                  }
                }}
              />
              <Typography
                variant="caption"
                color={remainingChars < 50 ? 'error' : 'text.secondary'}
                sx={{ display: 'block', textAlign: 'right', mt: 1 }}
              >
                {remainingChars} characters remaining
              </Typography>
            </Box>

            {/* Error Message */}
            {error && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="error" sx={{ 
                  backgroundColor: (theme) => 
                    theme.palette.mode === 'light' ? '#ffebee' : '#5a2020',
                  p: 2, 
                  borderRadius: 1,
                  border: (theme) => 
                    `1px solid ${theme.palette.mode === 'light' ? '#ffcdd2' : '#8a3030'}`
                }}>
                  {error}
                </Typography>
              </Box>
            )}

            {/* Actions - Moved here to be above keyboard */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: 3 }}>
              <Button
                onClick={handleClose}
                disabled={isSubmitting}
                variant="outlined"
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !reportMessage.trim() || showSuccess}
                variant="contained"
                startIcon={
                  showSuccess ? <CheckOutlined /> :
                  isSubmitting ? <CircularProgress size={16} /> : 
                  <SendOutlined />
                }
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 3,
                  backgroundColor: showSuccess ? '#4caf50' : '#4caf50',
                  '&:hover': {
                    backgroundColor: showSuccess ? '#4caf50' : '#45a049',
                  }
                }}
              >
                {showSuccess ? 'Message Sent!' : isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </Box>

            {/* Inline Success Message */}
            {showSuccess && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#4caf50',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1
                  }}
                >
                  <CheckOutlined sx={{ fontSize: 16 }} />
                  Your message has been sent to management
                </Typography>
              </Box>
            )}

          </Box>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default ReportModal;